/* eslint-disable no-bitwise, id-length, no-mixed-operators */
"use client"

import { useState, useEffect, useRef } from "react"
import { Play, Clock, Zap, Music, Layers, Video, Minus, Eye, Upload, Save, ImageIcon, Edit2Icon, Trash2Icon, Plus, Wand2, ChevronRight, Check } from "lucide-react"
import { Button } from "./ui/button"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs"
import { Slider } from "./ui/slider"
import { Label } from "./ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card"
import { Separator } from "./ui/separator"
import { Badge } from "./ui/badge"
import { ScrollArea } from "./ui/scroll-area"
import { Checkbox } from "./ui/checkbox"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog"
import { Input } from "./ui/input"
import { uploadLogoToSupabase } from "../API"
import { BUILTIN_PRESETS } from "../js/simulador/video/builtin-presets"
import { TakeEditorModal } from "../js/simulador/video/TakeEditorModal"

interface VideoMenuProps { canvas?: any }
type VideoType = "takes" | "auto" | "create" | "music" | "trans" | "conf"
interface CameraPoint { id: number; transitionTime: number; stayTime: number; effect: boolean }

const RESOLUTIONS = {
  landscape: [
    { label: "900×600", value: "900x600" },
    { label: "1280×720 (HD)", value: "1280x720" },
    { label: "1920×1080 (Full HD)", value: "1920x1080" },
    { label: "2560×1440 (2K)", value: "2560x1440" },
  ],
  portrait: [
    { label: "450×900", value: "450×900" },
    { label: "640×1280 (HD)", value: "640×1280" },
    { label: "960×1920 (Full HD)", value: "960x1920" },
    { label: "1280×2560 (2K)", value: "1280×2560" },
  ],
  square: [
    { label: "600×600", value: "600x600" },
    { label: "720×720 (HD)", value: "720x720" },
    { label: "1080×1080 (Full HD)", value: "1080x1080" },
    { label: "1920×1920 (2K)", value: "1920x1920" },
  ],
}

const FPS_OPTIONS = [
  { label: "20 fps", value: "20" },
  { label: "30 fps", value: "30" },
  { label: "60 fps", value: "60" },
  { label: "120 fps", value: "120" },
]

export default function VideoMenu({ canvas }: VideoMenuProps) {
  const [activeTab, setActiveTab] = useState<VideoType>("auto")
  const [cameraSpeed, setCameraSpeed] = useState(1)
  const [videoDuration, setVideoDuration] = useState(10)
  const [resolution, setResolution] = useState("900x600")
  const [fps, setFps] = useState("30")
  const [selectedMusic, setSelectedMusic] = useState<number | null>(null)
  const [playingMusic, setPlayingMusic] = useState<number | null>(null)
  const [playingTransition, setPlayingTransition] = useState<number | null>(null)
  const [cameraPoints, setCameraPoints] = useState<CameraPoint[]>([])
  const [logo1, setLogo1] = useState<string | null>(null)
  const [logo2, setLogo2] = useState<string | null>(null)
  const [savingLogos, setSavingLogos] = useState(false)
  const [activePresetId, setActivePresetId] = useState<string | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)

  const logo1InputRef = useRef<HTMLInputElement>(null)
  const logo2InputRef = useRef<HTMLInputElement>(null)
  const setLogo1Ref = useRef(setLogo1)
  const setLogo2Ref = useRef(setLogo2)
  setLogo1Ref.current = setLogo1
  setLogo2Ref.current = setLogo2

  const musicList = [
    { id: 0, name: "Sem Música" },
    ...Array.from({ length: 73 }, (_, i) => ({ id: i + 1, name: `Música ${i + 1}` })),
  ]
  const transitionList = Array.from({ length: 20 }, (_, i) => ({ id: i + 1, name: `Transição ${i + 1}` }))

  const setCameraPointsRef = useRef(setCameraPoints)
  setCameraPointsRef.current = setCameraPoints

  useEffect(() => {
    ;(window as any).video_time = (val: string) => setVideoDuration(Number.parseFloat(val))
    ;(window as any).video_speed = (val: string) => setCameraSpeed(Number.parseFloat(val))
    ;(window as any).addCameraPoint = () => {
      setCameraPointsRef.current((prev) => [...prev, { id: Date.now(), transitionTime: 3, stayTime: 3, effect: false }])
    }
    ;(window as any).loadLogos = (logo1Url: string | null, logo2Url: string | null) => {
      setLogo1Ref.current(logo1Url + "?" + Math.random())
      setLogo2Ref.current(logo2Url + "?" + Math.random())
    }
  }, [])

  useEffect(() => { ;(window as any).type_video = activeTab }, [activeTab])

  const handleTabChange = (value: string) => {
    const type = value as VideoType
    setActiveTab(type)
    ;(window as any).type_video = type
    if (type === "create") {
      ;(window as any).controls && ((window as any).controls.autoRotate = false)
    }
  }

  const handleApplyPreset = (presetItem: typeof BUILTIN_PRESETS[0]) => {
    setActivePresetId(presetItem.id)
    const w = window as any

    // Seta o preset ativo — Download.js lê ao iniciar a geração
    w.activeTakePreset = presetItem.preset
    w.type_video = 'takes'

    // Notifica com toast e instrui o usuário a clicar em Visualizar ou Gerar Vídeo
    // (não dispara automaticamente pois o contexto do simulador pode não estar pronto)
    if (typeof w.toast === 'function') {
      w.toast(`Preset "${presetItem.label}" selecionado. Clique em Visualizar ou Gerar Vídeo.`, 'success')
    }
  }

  const handleDeletePoint = (id: number, index: number) => {
    ;(window as any)["handleDeletePoint"](index)
    setCameraPoints((prev) => prev.filter((p) => p.id !== id))
  }

  const handleViewPoint = (index: number) => { ;(window as any).viewCameraPoint?.(index) }
  const handleDeleteAllPoints = () => setCameraPoints([])

  const handleUpdatePoint = (id: number, field: keyof CameraPoint, value: number | boolean) => {
    setCameraPoints((prev) => prev.map((p) => (p.id === id ? { ...p, [field]: value } : p)))
  }

  ;(window as any)["getResolutionVideo"] = function () {
    if (!resolution || !resolution.includes('x')) return { width: 640, height: 1280 }
    const [width, height] = resolution.split('x').map(Number)
    return { width: width || 640, height: height || 1280 }
  }
  ;(window as any)["getVideoDuration"] = function () { return videoDuration }
  ;(window as any)["getCameraSpeed"] = function () { return cameraSpeed }
  ;(window as any)["getLogo1"] = function () { return logo1 }
  ;(window as any)["getLogo2"] = function () { return logo2 }

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>, setLogo: (url: string | null) => void) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => setLogo(reader.result as string)
      reader.readAsDataURL(file)
    }
  }

  const handleSaveLogos = async () => {
    setSavingLogos(true)
    try {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      uploadLogoToSupabase("images", logo1, logo2)
    } catch { } finally { setSavingLogos(false) }
  }

  const handleColorChange = (e: any) => { ;(window as any).videoColor = e.target.value }

  const showVideoControls = activeTab === "conf"
  const showVideoPlay = activeTab === "takes" || activeTab === "auto" || activeTab === "create"

  return (
    <div className="flex h-full w-full flex-col bg-card/85 backdrop-blur-sm border-none p-2 rounded-lg" style={{ maxHeight: "100vh" }}>

      {/* Header */}
      <div className="border-b p-4">
        <div className="flex items-center gap-2">
          <Video className="h-5 w-5 text-primary" />
          <h2 className="text-lg font-semibold text-primary">Apresentação</h2>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Configure e gere vídeos do seu projeto. Não saia dessa tela durante a geração.
        </p>
      </div>

      <ScrollArea className="flex-1 border-none overflow-auto" style={{ minHeight: 0 }}>
        <div className="p-0 pt-3 space-y-4">

          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <TabsList className="grid w-full grid-cols-3 h-auto p-3">
              <TabsTrigger value="takes" data-type="takes" className="text-xs px-2 py-1.5 video-type">Takes</TabsTrigger>
              <TabsTrigger value="auto"  data-type="auto"  className="text-xs px-2 py-1.5 video-type">Rotação</TabsTrigger>
              <TabsTrigger value="create" data-type="create" className="text-xs px-2 py-1.5 video-type">Pontos</TabsTrigger>
              <TabsTrigger value="music" data-type="music" className="text-xs px-2 py-1.5 video-type">Música</TabsTrigger>
              <TabsTrigger value="trans" data-type="trans" className="text-xs px-2 py-1.5 video-type">Transição</TabsTrigger>
              <TabsTrigger value="conf"  data-type="conf"  className="text-xs px-2 py-1.5 video-type">Configurações</TabsTrigger>
            </TabsList>

            {/* ── TAKES PANEL ─────────────────────────────────────────────────── */}
            <TabsContent value="takes" className="mt-3 space-y-3">
              {/* Presets rápidos */}
              <Card>
                <CardHeader className="p-3 pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    <Wand2 className="h-4 w-4 text-primary" />
                    Presets de Animação
                  </CardTitle>
                  <CardDescription className="text-xs">Clique para aplicar diretamente</CardDescription>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-2">
                  {BUILTIN_PRESETS.map((item) => {
                    const isActive = activePresetId === item.id
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleApplyPreset(item)}
                        className="w-full text-left"
                      >
                        <div
                          style={{
                            border: `1px solid ${isActive ? item.accentColor : '#1e293b'}`,
                            background: isActive ? `${item.accentColor}15` : 'var(--muted)',
                            borderRadius: 8,
                            overflow: 'hidden',
                            transition: 'all 0.15s',
                          }}
                        >
                          {/* Vídeo preview */}
                          {item.videoUrl && (
                            <div style={{ position: 'relative', aspectRatio: '16/9', background: '#080e1a' }}>
                              <video
                                src={item.videoUrl}
                                muted autoPlay loop playsInline
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                              />
                              {isActive && (
                                <div style={{
                                  position: 'absolute', inset: 0,
                                  background: `${item.accentColor}22`,
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  <Check size={20} color={item.accentColor} />
                                </div>
                              )}
                            </div>
                          )}
                          {/* Info */}
                          <div style={{ padding: '6px 10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                              <div style={{ fontSize: 12, fontWeight: 600, color: isActive ? item.accentColor : 'inherit' }}>
                                {item.label}
                              </div>
                              <div style={{ fontSize: 10, color: '#64748b', marginTop: 1 }}>
                                {item.durationSec}s · {item.tags.slice(0, 2).join(' · ')}
                              </div>
                            </div>
                            {isActive
                              ? <Check size={13} color={item.accentColor} />
                              : <ChevronRight size={13} color="#64748b" />
                            }
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </CardContent>
              </Card>


            </TabsContent>

            {/* ── ROTATE PANEL ────────────────────────────────────────────────── */}
            <TabsContent value="auto" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="p-3 pb-2">
                  <CardDescription className="text-xs">A câmera gira ao redor do modelo.</CardDescription>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs flex items-center gap-1.5">
                        <Zap className="h-3.5 w-3.5" />Velocidade da Câmera
                      </Label>
                      <Badge variant="secondary" className="text-xs">{cameraSpeed}x</Badge>
                    </div>
                    <Slider id="video-speed-rotate" value={[cameraSpeed]} onValueChange={(v) => setCameraSpeed(v[0])} min={0} max={10} step={1} className="w-full" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label className="text-xs flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5" />Tempo (segundos)
                      </Label>
                      <Badge variant="secondary" className="text-xs">{videoDuration}s</Badge>
                    </div>
                    <Slider id="video-time" value={[videoDuration]} onValueChange={(v) => setVideoDuration(v[0])} min={1} max={60} step={1} className="w-full" />
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── POINTS PANEL ────────────────────────────────────────────────── */}
            <TabsContent value="create" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="p-3 pb-2">
                  <CardDescription className="text-xs">Adicione pontos de câmera para criar um caminho personalizado.</CardDescription>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <div className="flex gap-2">
                    <Button id="add-video-point" variant="outline" size="sm" className="flex-1 bg-transparent">
                      <Plus className="h-4 w-4 mr-1" />Adicionar
                    </Button>
                    <Button id="btn-delete-camera-points" variant="outline" size="sm" className="text-destructive hover:text-destructive bg-transparent" onClick={handleDeleteAllPoints}>
                      <Trash2Icon className="h-4 w-4" />
                    </Button>
                  </div>
                  <div id="video-points" className="mt-3 space-y-2 max-h-60 overflow-y-auto">
                    {cameraPoints.map((point, index) => (
                      <div key={point.id} className="panel-camera-points-card border rounded-lg p-3 bg-muted/30">
                        <div className="space-y-3">
                          <div className="input-video flex items-center justify-between gap-2">
                            <Label className="text-xs text-muted-foreground whitespace-nowrap">Tempo Transição</Label>
                            <Input type="number" className="time-video h-7 w-16 text-xs text-center" id={`time${index + 1}`} min={0} max={10} value={point.transitionTime} onChange={(e) => handleUpdatePoint(point.id, "transitionTime", Number(e.target.value))} />
                          </div>
                          <div className="input-video flex items-center justify-between gap-2">
                            <Label className="text-xs text-muted-foreground whitespace-nowrap">Tempo Olhando</Label>
                            <Input type="number" className="time-video-stay h-7 w-16 text-xs text-center" id={`time-stay-${index + 1}`} min={0} max={10} value={point.stayTime} onChange={(e) => handleUpdatePoint(point.id, "stayTime", Number(e.target.value))} />
                          </div>
                          <div className="input-video flex items-center justify-between gap-2">
                            <Label className="text-xs text-muted-foreground whitespace-nowrap">Efeito Transição</Label>
                            <Checkbox className={`effect effect-${index + 1}`} checked={point.effect} onCheckedChange={(checked) => handleUpdatePoint(point.id, "effect", !!checked)} />
                          </div>
                        </div>
                        <div className="options-point flex items-center justify-end gap-2 mt-3 pt-2 border-t">
                          <Button variant="ghost" size="sm" className="btn-view-point h-7 w-7 p-0" data-pos={index} onClick={() => handleViewPoint(index)}>
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" className="btn-delete-point h-7 w-7 p-0 text-destructive hover:text-destructive" data-pos={index} onClick={() => handleDeletePoint(point.id, index)}>
                            <Minus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── MUSIC PANEL ─────────────────────────────────────────────────── */}
            <TabsContent value="music" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="p-3 pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    <Music className="h-4 w-4" />Música de Fundo
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0 pt-0">
                  <div id="player-music" className="mb-3 flex justify-center ml-[10%]" />
                  <ScrollArea className="h-[400px]">
                    <div className="musics space-y-1">
                      {musicList.map((music) => (
                        <div key={music.id} className="music-option flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors" data-id={music.id}>
                          <label htmlFor={`check-music-${music.id}`} className="flex-1 text-xs cursor-pointer select-none">{music.name}</label>
                          {music.id !== 0 && (
                            <button type="button" className="play-music p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" id={`music-${music.id}`} onClick={() => setPlayingMusic(playingMusic === music.id ? null : music.id)}>
                              <Play className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── TRANSITION PANEL ────────────────────────────────────────────── */}
            <TabsContent value="trans" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="p-3 pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    <Layers className="h-4 w-4" />Transições
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0">
                  <ScrollArea className="h-[400px]">
                    <div className="transitions space-y-1">
                      {transitionList.map((transition) => (
                        <div key={transition.id} className="transition-option flex items-center gap-2 p-2 rounded-md hover:bg-muted/50 transition-colors" data-id={transition.id}>
                          <label htmlFor={`check-transition-${transition.id}`} className="flex-1 text-xs cursor-pointer select-none">{transition.name}</label>
                          <button type="button" className="play-transition p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" id={`transition-${transition.id}`} onClick={() => setPlayingTransition(playingTransition === transition.id ? null : transition.id)}>
                            <Play className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ── CONFIG PANEL ────────────────────────────────────────────────── */}
            <TabsContent value="conf" className="mt-4 space-y-4">
              <Card>
                <CardHeader className="p-3 pb-2">
                  <CardTitle className="text-sm">Configurações de Saída</CardTitle>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Resolução</Label>
                    <Select value={resolution} onValueChange={setResolution}>
                      <SelectTrigger id="active-option-res" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Paisagem</div>
                        {RESOLUTIONS.landscape.map((r) => <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>)}
                        <Separator className="my-1" />
                        <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Retrato</div>
                        {RESOLUTIONS.portrait.map((r) => <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>)}
                        <Separator className="my-1" />
                        <div className="px-2 py-1 text-xs font-medium text-muted-foreground">Quadrado</div>
                        {RESOLUTIONS.square.map((r) => <SelectItem key={r.value} value={r.value} className="text-xs">{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Taxa de Quadros</Label>
                    <Select value={fps} onValueChange={setFps}>
                      <SelectTrigger id="active-option-fps" className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {FPS_OPTIONS.map((opt) => <SelectItem key={opt.value} value={opt.value} className="text-xs">{opt.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Logos */}
              <Card>
                <CardHeader className="p-3 pb-2">
                  <CardTitle className="text-sm flex items-center gap-1.5">
                    <ImageIcon className="h-4 w-4" />Logos
                  </CardTitle>
                  <CardDescription className="text-xs">Selecione as logos para o vídeo</CardDescription>
                </CardHeader>
                <CardContent className="p-3 pt-0 space-y-3">
                  <div className="space-y-2">
                    <Label className="text-xs">Cor Luz Vídeo</Label>
                    <input defaultValue="#ffffff" onChange={handleColorChange} type="color" />
                  </div>
                  {[{ num: 1, logo: logo1, setLogo: setLogo1, ref: logo1InputRef }, { num: 2, logo: logo2, setLogo: setLogo2, ref: logo2InputRef }].map(({ num, logo, setLogo, ref }) => (
                    <div key={num} className="space-y-2">
                      <Label className="text-xs">Logo {num}</Label>
                      <div className="flex items-center gap-2">
                        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={(e) => handleLogoChange(e, setLogo)} />
                        <Button variant="outline" size="sm" className="flex-1 bg-transparent" onClick={() => ref.current?.click()}>
                          <Upload className="h-4 w-4 mr-2" />{logo ? "Alterar" : "Selecionar"}
                        </Button>
                        {logo && <div className="h-8 w-8 rounded border overflow-hidden"><img id={`logo-video${num}`} src={logo} alt={`Logo ${num}`} className="h-full w-full object-contain" /></div>}
                        <button className="bg-[red] h-[30px] w-[30px] flex items-center justify-center rounded-lg delete-logo-video" data-path={num}>
                          <Trash2Icon />
                        </button>
                      </div>
                    </div>
                  ))}
                  <Separator />
                  <Button className="w-full" size="sm" onClick={handleSaveLogos} disabled={savingLogos || (!logo1 && !logo2)}>
                    <Save className="h-4 w-4 mr-2" />{savingLogos ? "Salvando..." : "Salvar Logos"}
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

          </Tabs>
        </div>
      </ScrollArea>

      {/* Footer */}
      {showVideoPlay && (
        <div className="border-t p-3 space-y-2 flex-shrink-0">
          <Button
            id="btn-show-take-editor"
            type="button"
            variant="outline"
            size="sm"
            className="w-full bg-transparent"
            onClick={() => setEditorOpen(true)}
          >
            <Edit2Icon className="h-4 w-4 mr-2" />Editor avançado
          </Button>
          <Button id="btn-start-camera22" variant="outline" className="w-full bg-white" size="sm">
            <Play className="h-4 w-4 mr-2" />Visualizar
          </Button>
          <Button
            id="btn-generate-video"
            className="w-full canvas_capture_toggle bg-background text-primary"
            size="sm"
            data-index="7"
            data-canvas-is-local="true"
            data-frame-u-u-i-d="top"
            data-canvas-index="7"
            data-width={resolution.split("x")[0]}
            data-height={resolution.split("x")[1]}
          >
            <Video className="h-4 w-4 mr-2" />Gerar Vídeo
          </Button>
        </div>
      )}
      {/* TakeEditorModal — fullscreen portal, fora da sidebar */}
      {editorOpen && (
        <TakeEditorModal
          projectId={canvas?.projectId ?? 'default'}
          initialResolution={resolution}
          onClose={() => setEditorOpen(false)}
          onApply={(appliedResolution) => {
            if (appliedResolution) {
              setResolution(appliedResolution)
              delete (window as any)._previewResolution
            }
          }}
        />
      )}
    </div>
  )
}